import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { ChartCategoryResponse, RecordResponse } from '../models/ServiceTypes';
import type { AdvisorInvestor } from './usersApi';
import type { SummedValue } from './diversificationsApi';
import type {
  InvestorGrouping,
  InvestorGroupingDetails,
  CreateInvestorGroupingRequest,
  CreateInvestorGroupingResponse,
  UpdateInvestorGroupingRequest,
  InvestorGroupSubGroup,
  InvestorSubGroupRequest,
  SetSegmentAssignmentsRequest,
  SetSegmentAssignmentsResponse,
  UpdateGroupSegmentsRequest,
  GroupSegmentsResponse,
} from '@/interfaces/investorGrouping';

// Response from setup investors endpoint may come as either a RecordResponse
// or a bare array (legacy). Capture both to avoid unsafe casts.
type AdvisorInvestorsListResponse =
  | RecordResponse<AdvisorInvestor>
  | AdvisorInvestor[]
  | {
      Records?: AdvisorInvestor[];
      TotalLength?: number;
      SummedValue?: number | null;
    };

type StringNumberBoolean = string | number | boolean;
type QueryParams = Record<string, StringNumberBoolean | null | undefined>;

// Named aliases for the response-cast unions below. Kept out of inline `as`
// casts because Prettier 3.8 and 3.9 wrap cast-unions differently (3.9 collapses
// them), which fails CI's format check; type-alias unions format identically
// across both versions.
type MaybeRecordResponse = {
  Records?: AdvisorInvestor[];
  TotalLength?: number;
};

/**
 * `/api/advisor/investors` response. `SummedValues` holds one entry per
 * property requested through `sum`, computed over the full filtered set before
 * paging. The server omits it when nothing was requested or nothing matched.
 */
export type AdvisorInvestorsResponse = RecordResponse<AdvisorInvestor> & {
  SummedValues?: SummedValue[] | null;
};

/**
 * Onboarding status ids from `uspGetFirmInvestorOnboardingStatus`. `OnlyOrion`
 * is returned only by firms still on that integration, so for nearly everyone
 * it is absent rather than present at zero.
 */
export enum OnboardingStatusId {
  NotInvited = 1,
  Invited = 2,
  Active = 3,
  OnlyOrion = 4,
}

/** One row of `GET /api/advisor/prospects` (backend `ProspectModel`). */
export type Prospect = {
  InvestorId: number;
  UserId: number;
  FirstName: string;
  LastName: string;
  Name: string;
  UserName: string | null;
  OnboardingStep: number;
  OnboardingStepDisplay: string;
  IsConfirmed: boolean;
  HasProfile: boolean;
  ProfilePercent: number;
  NumberOfAccounts: number;
  NumberOfSignatureRequests: number;
  NumberOfSignedFiles: number;
  SignedPercent: number;
  CanImpersonate: boolean;
};

export type ProspectsQuerySort = {
  Property: keyof Prospect;
  Direction: 'asc' | 'desc';
};

export type ProspectsQueryArgs = {
  /** 1-based, as the server pages. */
  page: number;
  sort: ProspectsQuerySort[];
  /** Matches the name or the username; the server ignores case. */
  searchTerm?: string;
};

const toProspectsQueryParams = ({
  page,
  sort,
  searchTerm,
}: ProspectsQueryArgs): QueryParams => {
  const params: QueryParams = { page };
  if (searchTerm) params.SearchTerm = searchTerm;
  sort.forEach((s, idx) => {
    params[`sort[${String(idx)}].Property`] = s.Property;
    params[`sort[${String(idx)}].Direction`] = s.Direction;
    params[`sort[${String(idx)}].Order`] = idx + 1;
  });
  return params;
};

/**
 * Normalizes the polymorphic `advisor/setup/investors` response — which may be
 * a RecordResponse, a bare array, a partial object, or null/empty (legacy) —
 * into a stable RecordResponse. Property access is guarded so a null/empty body
 * can't throw.
 */
function normalizeSetupInvestorsResponse(
  raw: AdvisorInvestorsListResponse | null | undefined,
): RecordResponse<AdvisorInvestor> {
  const obj = raw as MaybeRecordResponse | null | undefined;

  // Already a RecordResponse-shaped payload: pass it through untouched.
  if (obj?.Records) {
    return raw as RecordResponse<AdvisorInvestor>;
  }

  // Otherwise build one from a bare array, or fall back to empty.
  const records = Array.isArray(raw) ? raw : [];
  const totalLength = Array.isArray(raw) ? raw.length : (obj?.TotalLength ?? 0);
  return { Records: records, TotalLength: totalLength, SummedValue: null };
}

export const advisorInvestorsApi = createApi({
  reducerPath: 'advisorInvestorsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'AdvisorInvestorsList',
    'AdvisorSetupInvestors',
    'InvestorsOnboardingChart',
    'AdvisorInvestorById',
    'AdvisorThirdParty',
    'InvestorGroupings',
    'AdvisorProspects',
  ],
  endpoints: (builder) => ({
    getAdvisorInvestors: builder.query<
      AdvisorInvestorsResponse,
      {
        searchTerm?: string;
        page?: number;
        groupId?: number;
        groupParentId?: number;
        groupValueProperty?: string; // e.g., "OnboardingStep" or dynamic group field
        groupDisplayProperty?: string; // e.g., "OnboardingStepDisplay"
        groupSortProperty?: string; // e.g., "OnboardingStep"
        excludedOnboardingSteps?: string; // e.g., "2,3" for post-household creation parity
        sort?: {
          Property: string;
          Direction: 'asc' | 'desc';
          Order?: number;
        }[];
        includeDefaults?: boolean; // when false, do not include default params
        timestampMs?: number; // when provided, sent as _ param
        excludedInvestorTypes?: string | number[]; // optional; used by legacy setup grid to exclude Estates(2) and Households(3)
        limit?: number; // optional; override page size
        sum?: string[]; // properties to total over the full filtered set, e.g. ['Home']
      }
    >({
      queryFn: async (args, _queryApi, _extra, baseQuery) => {
        const {
          searchTerm,
          groupId,
          page,
          groupParentId,
          groupValueProperty,
          groupDisplayProperty,
          groupSortProperty,
          excludedOnboardingSteps,
          sort,
          includeDefaults = true,
          timestampMs,
          excludedInvestorTypes,
          limit,
          sum,
        } = args;

        // Base params
        const params: QueryParams = {};
        if (includeDefaults) {
          params['sort[0].Property'] = 'Name';
          params['sort[0].Direction'] = 'asc';
          params['sort[0].Order'] = 1;
          params.AlwaysShowHouseholds = 'true';
        }
        if (excludedInvestorTypes !== undefined) {
          if (Array.isArray(excludedInvestorTypes)) {
            params.ExcludedInvestorTypes = excludedInvestorTypes.join(',');
          } else {
            params.ExcludedInvestorTypes = excludedInvestorTypes;
          }
        }
        // Apply custom sort overrides when provided
        if (Array.isArray(sort) && sort.length > 0) {
          sort.forEach((s, idx) => {
            params[`sort[${String(idx)}].Property`] = s.Property;
            params[`sort[${String(idx)}].Direction`] = s.Direction;
            params[`sort[${String(idx)}].Order`] = s.Order ?? 1;
          });
        }
        // Indexed like sort[] so the server binds the array; each entry comes
        // back in SummedValues, summed before paging.
        if (Array.isArray(sum)) {
          sum.forEach((property, idx) => {
            params[`sum[${String(idx)}]`] = property;
          });
        }
        if (groupId) params.GroupId = groupId;
        if (page !== undefined) params.page = page;
        if (typeof limit === 'number') params.limit = limit;

        // Manually construct query string to match legacy serialization and parameter casing
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        if (searchTerm !== undefined) {
          // Legacy uses capitalized SearchTerm
          searchParams.append('SearchTerm', searchTerm);
        }
        if (excludedOnboardingSteps) {
          searchParams.append(
            'ExcludedOnboardingSteps',
            excludedOnboardingSteps,
          );
        }

        if (groupParentId) {
          const field = `group${String(groupParentId)}`;
          // Correctly format grouping params with brackets, e.g., group[ValueProperty]
          searchParams.append('group[ValueProperty]', field);
          searchParams.append('group[DisplayProperty]', field);
        }

        // Explicit grouping props take precedence if provided
        if (groupValueProperty) {
          searchParams.set('group[ValueProperty]', groupValueProperty);
        }
        if (groupDisplayProperty) {
          searchParams.set('group[DisplayProperty]', groupDisplayProperty);
        }
        if (groupSortProperty) {
          searchParams.set('group[SortProperty]', groupSortProperty);
        }

        if (typeof timestampMs === 'number') {
          searchParams.append('_', String(timestampMs));
        }
        const queryString = searchParams.toString();
        const url = `/api/advisor/investors${queryString ? `?${queryString}` : ''}`;

        const result = await baseQuery({
          url,
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        // Restore client-side record parsing for robustness, matching legacy exactly.
        const data = result.data as AdvisorInvestorsResponse;
        // Records is required per the type, but the legacy API can return a
        // malformed/empty body at runtime; read it through a nullable view so
        // the defensive guard stays honest (matches normalizeSetupInvestorsResponse).
        const maybe = result.data as MaybeRecordResponse | null | undefined;

        if (maybe?.Records) {
          data.Records = maybe.Records.map((investor) => {
            if (Array.isArray(investor.Groups)) {
              investor.Groups.forEach((group) => {
                const g = group as {
                  ParentId?: number | string | null;
                  Name?: string | null;
                } | null;
                const parentId = g?.ParentId;
                const name = g?.Name;
                if (parentId != null && name) {
                  (
                    investor as unknown as Record<
                      string,
                      StringNumberBoolean | null
                    >
                  )[`group${String(parentId)}`] = name;
                }
              });
            }
            return investor;
          });
        }

        return { data };
      },
      providesTags: (_result) => [{ type: 'AdvisorInvestorsList', id: 'LIST' }],
    }),
    // Get single advisor investor by id (legacy parity)
    getAdvisorInvestorById: builder.query<AdvisorInvestor, number>({
      queryFn: async (investorId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investors/${String(investorId)}`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as AdvisorInvestor };
      },
      providesTags: (_res, _err, id) => [{ type: 'AdvisorInvestorById', id }],
    }),
    // Advisor thirdparty flag by investorId (legacy parity)
    getAdvisorThirdParty: builder.query<boolean, number>({
      queryFn: async (investorId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/thirdparty`,
          method: 'GET',
          params: { investorId },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as boolean };
      },
      providesTags: (_res, _err, id) => [{ type: 'AdvisorThirdParty', id }],
    }),
    getInvestorGroups: builder.query<
      RecordResponse<InvestorGrouping>,
      { searchTerm?: string; page?: number } | undefined
    >({
      queryFn: async (args, _queryApi, _extra, baseQuery) => {
        const params: QueryParams = {};
        if (args?.searchTerm) {
          params.searchTerm = args.searchTerm;
        }
        if (args?.page) {
          params.page = args.page;
        }
        const result = await baseQuery({
          url: `/api/advisor/investorGroups`,
          method: 'GET',
          params,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<InvestorGrouping> };
      },
      providesTags: [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    getInvestorGroupingsSimple: builder.query<
      RecordResponse<InvestorGrouping>,
      undefined
    >({
      queryFn: async (_arg, _queryApi, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/SImple`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<InvestorGrouping> };
      },
      // 'SIMPLE' feeds the Clients "Group by" picker, so a delete can target it
      // precisely (dropping the deleted group from the dropdown) without force-
      // refetching the modal grid ('DETAILS'). 'LIST' is retained so existing
      // group create/add mutations still refetch the picker.
      providesTags: () => [
        { type: 'InvestorGroupings', id: 'SIMPLE' },
        { type: 'InvestorGroupings', id: 'LIST' },
      ],
    }),
    // Detailed groupings for the Manage Client Groups modal: one call returns
    // every group with its segments (Groups) and assignment counts, so the whole
    // grid renders from this single response.
    getInvestorGroupingsWithDetails: builder.query<
      RecordResponse<InvestorGroupingDetails>,
      undefined
    >({
      queryFn: async (_arg, _queryApi, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return {
          data: result.data as RecordResponse<InvestorGroupingDetails>,
        };
      },
      // 'DETAILS' is the modal grid. The delete mutation patches this cache
      // optimistically, so it deliberately does NOT invalidate 'DETAILS' — a
      // flaky refetch would blank the modal.
      providesTags: () => [{ type: 'InvestorGroupings', id: 'DETAILS' }],
    }),
    // Legacy-parity: advisor/setup/investors - plain GET (no default params)
    getAdvisorSetupInvestors: builder.query<
      RecordResponse<AdvisorInvestor>,
      undefined | QueryParams
    >({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const hasParams = !!arg && Object.keys(arg).length > 0;
        const result = await baseQuery({
          url: `/api/advisor/setup/investors`,
          method: 'GET',
          ...(hasParams ? { params: arg } : {}),
        });
        if (result.error) return { error: result.error };
        const raw = result.data as AdvisorInvestorsListResponse;
        return { data: normalizeSetupInvestorsResponse(raw) };
      },
      providesTags: (_res) => [{ type: 'AdvisorSetupInvestors', id: 'LIST' }],
    }),
    // The server scopes the list to the signed in advisor and pages it.
    getProspects: builder.query<RecordResponse<Prospect>, ProspectsQueryArgs>({
      query: (args) => ({
        url: `/api/advisor/prospects`,
        method: 'GET',
        params: toProspectsQueryParams(args),
      }),
      providesTags: (_res) => [{ type: 'AdvisorProspects', id: 'LIST' }],
    }),
    // RTK Query also invalidates on a server error. The grid then drops a
    // prospect that someone else converted in the meantime.
    convertProspect: builder.mutation<boolean, number>({
      query: (investorId) => ({
        url: `/api/advisor/prospects/${String(investorId)}/convert`,
        method: 'PUT',
      }),
      invalidatesTags: () => [
        { type: 'AdvisorProspects', id: 'LIST' },
        { type: 'AdvisorInvestorsList', id: 'LIST' },
        { type: 'AdvisorSetupInvestors', id: 'LIST' },
        { type: 'InvestorsOnboardingChart', id: 'LIST' },
      ],
    }),
    // Legacy-parity: investorsOnboardingChart - summary chart counts
    getInvestorsOnboardingChart: builder.query<
      ChartCategoryResponse,
      undefined
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorsOnboardingChart`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as ChartCategoryResponse };
      },
      providesTags: (_res) => [
        { type: 'InvestorsOnboardingChart', id: 'LIST' },
      ],
    }),
    // Bulk delete investors with automatic invalidation of related queries
    bulkDeleteInvestors: builder.mutation<
      { success: boolean } | undefined,
      { UserIds: number[] }
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investors/bulkDelete`,
          method: 'POST',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      invalidatesTags: () => [
        { type: 'AdvisorInvestorsList', id: 'LIST' },
        { type: 'AdvisorSetupInvestors', id: 'LIST' },
        { type: 'InvestorsOnboardingChart', id: 'LIST' },
        { type: 'AdvisorProspects', id: 'LIST' },
      ],
    }),
    // Sub-groups (segments) belonging to a named investor grouping. The
    // create-grouping response returns segment name strings only, so this is
    // the source for mapping a segment name -> its AdvisorNamedInvestorGroupId.
    getInvestorGroupSubGroups: builder.query<
      RecordResponse<InvestorGroupSubGroup>,
      number
    >({
      queryFn: async (parentGroupId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(parentGroupId)}/subGroups`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<InvestorGroupSubGroup> };
      },
      providesTags: [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    // Investors not yet assigned to any sub-group of a named grouping
    getUnassignedInvestors: builder.query<
      RecordResponse<AdvisorInvestor>,
      { advisorNamedInvestorGroupId: number; page?: number; limit?: number }
    >({
      queryFn: async (
        { advisorNamedInvestorGroupId, page, limit },
        _api,
        _extra,
        baseQuery,
      ) => {
        const params: QueryParams = {};
        if (page !== undefined) params.page = page;
        if (typeof limit === 'number') params.limit = limit;
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(advisorNamedInvestorGroupId)}/Unassigned`,
          method: 'GET',
          params,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<AdvisorInvestor> };
      },
      providesTags: [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    // Add an investor to an existing investor group
    addInvestorToGroup: builder.mutation<
      { success: boolean } | undefined,
      { advisorNamedInvestorGroupId: number; investorId: number }
    >({
      queryFn: async (
        { advisorNamedInvestorGroupId, investorId },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroups/${String(advisorNamedInvestorGroupId)}/investors`,
          method: 'POST',
          body: {
            AdvisorNamedInvestorGroupId: advisorNamedInvestorGroupId,
            InvestorId: investorId,
          },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      invalidatesTags: () => [
        { type: 'AdvisorInvestorsList', id: 'LIST' },
        { type: 'InvestorGroupings', id: 'LIST' },
      ],
    }),
    setSegmentAssignments: builder.mutation<
      SetSegmentAssignmentsResponse,
      SetSegmentAssignmentsRequest
    >({
      queryFn: async (
        { parentGroupId, Assignments },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/advisor/InvestorGroupings/${String(parentGroupId)}/assignments`,
          method: 'PATCH',
          body: { Assignments },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as SetSegmentAssignmentsResponse };
      },
      invalidatesTags: () => [{ type: 'AdvisorInvestorsList', id: 'LIST' }],
    }),
    updateGroupSegments: builder.mutation<
      GroupSegmentsResponse,
      UpdateGroupSegmentsRequest
    >({
      queryFn: async ({ parentGroupId, ...body }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/InvestorGroupings/${String(parentGroupId)}/segments`,
          method: 'PATCH',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as GroupSegmentsResponse };
      },
      invalidatesTags: () => [
        { type: 'InvestorGroupings', id: 'LIST' },
        { type: 'AdvisorInvestorsList', id: 'LIST' },
      ],
    }),
    // Create a named investor grouping (custom segment)
    createInvestorGrouping: builder.mutation<
      CreateInvestorGroupingResponse,
      CreateInvestorGroupingRequest
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings`,
          method: 'POST',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as CreateInvestorGroupingResponse };
      },
      invalidatesTags: () => [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    // Update a named investor grouping (custom segment)
    updateInvestorGrouping: builder.mutation<
      { success: boolean } | undefined,
      UpdateInvestorGroupingRequest
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(body.AdvisorNamedInvestorGroupId)}`,
          method: 'PUT',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      invalidatesTags: () => [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    // Create a sub-group (segment) under a named investor grouping
    createInvestorSubGroup: builder.mutation<
      { success: boolean } | undefined,
      InvestorSubGroupRequest
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(body.ParentId)}/subGroups`,
          method: 'POST',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      invalidatesTags: () => [{ type: 'InvestorGroupings', id: 'LIST' }],
    }),
    // Update a sub-group (segment) under a named investor grouping
    updateInvestorSubGroup: builder.mutation<
      { success: boolean } | undefined,
      InvestorSubGroupRequest
    >({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(body.ParentId)}/subGroups/${String(body.AdvisorNamedInvestorGroupId)}`,
          method: 'PUT',
          body,
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      // Each investor row embeds its segments in `Groups` (name included), so a
      // rename stales the Clients grid — including the values it derives when
      // grouped by this client group.
      invalidatesTags: () => [
        { type: 'InvestorGroupings', id: 'LIST' },
        { type: 'AdvisorInvestorsList', id: 'LIST' },
      ],
    }),
    // Delete a sub-group (segment) under a named investor grouping
    deleteInvestorSubGroup: builder.mutation<
      { success: boolean } | undefined,
      { parentGroupId: number; advisorNamedInvestorGroupId: number }
    >({
      queryFn: async (
        { parentGroupId, advisorNamedInvestorGroupId },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroupings/${String(parentGroupId)}/subGroups/${String(advisorNamedInvestorGroupId)}`,
          method: 'DELETE',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      // Deleting a segment drops it from every assigned investor's `Groups`, so
      // the Clients grid would otherwise keep listing those clients under it.
      invalidatesTags: () => [
        { type: 'InvestorGroupings', id: 'LIST' },
        { type: 'AdvisorInvestorsList', id: 'LIST' },
      ],
    }),
    // Remove an investor from an existing investor group
    removeInvestorFromGroup: builder.mutation<
      { success: boolean } | undefined,
      { advisorNamedInvestorGroupId: number; investorId: number }
    >({
      queryFn: async (
        { advisorNamedInvestorGroupId, investorId },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/advisor/investorGroups/${String(advisorNamedInvestorGroupId)}/investors/${String(investorId)}`,
          method: 'DELETE',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as { success: boolean } | undefined };
      },
      invalidatesTags: () => [
        { type: 'AdvisorInvestorsList', id: 'LIST' },
        { type: 'InvestorGroupings', id: 'LIST' },
      ],
    }),
    // Delete a custom client group (IU-17). Optimistically removes the row from
    // the Manage modal grid so it disappears instantly; reverts if the request
    // fails. On success invalidates InvestorGroupings so the Group By picker
    // (Simple) and any other grouping consumers refetch and drop the group too.
    deleteInvestorGrouping: builder.mutation<
      boolean,
      { AdvisorNamedInvestorGroupId: number }
    >({
      queryFn: async (
        { AdvisorNamedInvestorGroupId },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: `/api/advisor/InvestorGroupings/${String(AdvisorNamedInvestorGroupId)}`,
          method: 'DELETE',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as boolean };
      },
      onQueryStarted: async (
        { AdvisorNamedInvestorGroupId },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistically drop the group from BOTH grouping caches so it
        // disappears instantly from the modal grid (DETAILS) and the Clients
        // "Group by" picker (SIMPLE) — and stays gone even if the reconciling
        // refetch below fails. Both patches are reverted if the delete fails.
        const patches = [
          dispatch(
            advisorInvestorsApi.util.updateQueryData(
              'getInvestorGroupingsWithDetails',
              undefined,
              (draft) => {
                // Defensive: the cached body comes from the API. Guard against a
                // malformed response missing Records (the modal's render path
                // tolerates it the same way) before filtering.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!draft.Records) return;
                draft.Records = draft.Records.filter(
                  (g) =>
                    g.AdvisorNamedInvestorGroupId !==
                    AdvisorNamedInvestorGroupId,
                );
              },
            ),
          ),
          dispatch(
            advisorInvestorsApi.util.updateQueryData(
              'getInvestorGroupingsSimple',
              undefined,
              (draft) => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!draft.Records) return;
                draft.Records = draft.Records.filter(
                  (g) => g.Id !== AdvisorNamedInvestorGroupId,
                );
              },
            ),
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          // Request failed: restore both optimistically removed rows.
          patches.forEach((p) => {
            p.undo();
          });
        }
      },
      // Reconcile the 'Group by' picker ('SIMPLE') with the server on success.
      // Both grouping caches are already patched above, so a failed refetch
      // keeps the group removed; the modal grid ('DETAILS') isn't invalidated
      // because it surfaces isError and a flaky refetch would blank the modal.
      // The Clients grid is invalidated because deleting a group drops all of
      // its segments from every assigned investor's `Groups`, which those rows
      // embed by name.
      invalidatesTags: () => [
        { type: 'InvestorGroupings', id: 'SIMPLE' },
        { type: 'AdvisorInvestorsList', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAdvisorInvestorsQuery,
  useLazyGetAdvisorInvestorsQuery,
  useGetInvestorGroupsQuery,
  useGetInvestorGroupingsSimpleQuery,
  useGetInvestorGroupingsWithDetailsQuery,
  useLazyGetInvestorGroupingsWithDetailsQuery,
  useGetAdvisorSetupInvestorsQuery,
  useLazyGetAdvisorSetupInvestorsQuery,
  useGetInvestorsOnboardingChartQuery,
  useGetProspectsQuery,
  useConvertProspectMutation,
  useLazyGetInvestorsOnboardingChartQuery,
  useBulkDeleteInvestorsMutation,
  useDeleteInvestorGroupingMutation,
  useGetAdvisorInvestorByIdQuery,
  useGetAdvisorThirdPartyQuery,
  useCreateInvestorGroupingMutation,
  useUpdateInvestorGroupingMutation,
  useCreateInvestorSubGroupMutation,
  useUpdateInvestorSubGroupMutation,
  useDeleteInvestorSubGroupMutation,
  useAddInvestorToGroupMutation,
  useRemoveInvestorFromGroupMutation,
  useSetSegmentAssignmentsMutation,
  useUpdateGroupSegmentsMutation,
  useGetUnassignedInvestorsQuery,
  useLazyGetUnassignedInvestorsQuery,
  useGetInvestorGroupSubGroupsQuery,
  useLazyGetInvestorGroupSubGroupsQuery,
} = advisorInvestorsApi;
